import { Component, HostListener, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CompassAnswerable, CompassAnswerableButton, CompassAnswerableType, CompassAssessment, DateUtil, Time } from 'huddyapi';
import { DateSelectDialog, DateSelectDialogResult } from '../date-select-dialog/date-select-dialog.component';

type Selection = string | Map<string, void> | number;

@Component({
  selector: 'assessment-preview-dialog',
  templateUrl: 'assessment-preview-dialog.component.html',
  styleUrls: ["./assessment-preview-dialog.component.scss"]
})
export class AssessmentPreviewDialog {
  static NUM_BACKGROUNDS: number = 4;

  cardIndex: number;
  cardCount: number;
  lastMove: number;
  progressArray: void[];
  selections: Selection[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AssessmentPreviewDialogData,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<AssessmentPreviewDialog>
  ) {
    this.cardCount = this.getQuestions().length + 1;
    this.cardIndex = data.startIndex == null ? 0 : Math.min(this.cardCount - 1, Math.max(0, data.startIndex));
    this.progressArray = [];
    for(let i = 0; i < this.cardCount; i++)
      this.progressArray.push(null);
    this.selections = this.getQuestions().map((question: CompassAnswerable) => {
      if(question.answerType == CompassAnswerableType.BUTTON && question.multiAnswer)
        return new Map<string, void>();
      return null;
    });
  }

  canMove(): boolean {
    return this.lastMove == null || new Date().getTime() > this.lastMove + 200;
  }

  move(forward: boolean): void {
    if(!this.canMove() || (forward ? this.cardIndex >= this.cardCount - 1 : this.cardIndex <= 0))
      return;
    this.lastMove = new Date().getTime();
    this.cardIndex += forward ? 1 : -1;
  }

  handleCardClick(index: number): void {
    if(Math.abs(index - this.cardIndex) == 1)
      this.move(index > this.cardIndex);
  }

  getCardClasses(index: number): any {
    const diff: number = Math.abs(index - this.cardIndex);
    return {
      showing: diff < 2,
      left: diff < 3 && index < this.cardIndex,
      right: diff < 3 && index > this.cardIndex
    };
  }

  getQuestions(): CompassAnswerable[] {
    return this.data?.assessment?.questions || [];
  }

  getBackgroundClass(): string {
    return 'b' + (this.cardIndex % AssessmentPreviewDialog.NUM_BACKGROUNDS);
  }

  getProgressBarStyle(): string {
    return '--progress:' + ((2 * this.cardIndex + 1) / (2 * this.cardCount) * 100) + '%' + ';';
  }

  getQuestion(index: number): CompassAnswerable {
    return this.getQuestions()[index];
  }

  checkType(index: number, type: CompassAnswerableType): boolean {
    return this.getQuestion(index)?.answerType == type;
  }

  typeIsButton(index: number): boolean {
    return this.checkType(index, CompassAnswerableType.BUTTON);
  }

  typeIsInput(index: number): boolean {
    return this.checkType(index, CompassAnswerableType.INPUT);
  }

  typeIsDate(index: number): boolean {
    return this.checkType(index, CompassAnswerableType.DATE);
  }

  getImgSrc(index: number): string {
    let ret = './assets/';
    if(index == this.getQuestions().length) {
      ret = ret + 'ic_check-circle.svg';
    } else {
      let mod = index % 4;
      ret = ret + "covid19/ic_covid19_" + (mod + 1) + '.svg';
    }
    return ret;
  }

  hasDate(index: number): boolean {
    return this.selections[index] != null;
  }

  getDateIcon(index: number): string {
    const day: number = this.selections[index] as number;
    return './assets/content-icon/ic-' + (day == null ? '09' : '18') + '.svg'
  }

  getDateText(index: number): string {
    const day: number = this.selections[index] as number;
    if(day == null)
      return 'Choose a date';
    return this.getDateString(day);
  }

  getDateString(day: number): string {
    return DateUtil.fromEpochDay(day).momentVal.format('ddd, MMM Do YYYY');
  }

  hasAttribution(): boolean {
    return this.data?.assessment?.attribution != null;
  }

  hasAttributionLink(): boolean {
    return this.data?.assessment?.attributionLink != null;
  }

  getAnswerSelected(index: number, button: CompassAnswerableButton): boolean {
    if(index >= this.getQuestions().length)
      return false;
    const selection: Selection = this.selections[index];
    return selection instanceof Map ? (selection as Map<string, void>).has(button.answer) : (selection as string) == button.answer;
  }

  buttonClicked(button?: CompassAnswerableButton): void {
    if(this.cardIndex >= this.cardCount - 1) {
      this.dialogRef.close();
      return;
    }
    if(!button)
      return;
    const selection: Selection = this.selections[this.cardIndex];
    if(selection instanceof Map) {
      if(selection.has(button.answer))
        selection.delete(button.answer);
      else
        selection.set(button.answer);
    } else
      this.selections[this.cardIndex] = button.answer;
  }

  chooseDate(index: number): void {
    if(index >= this.getQuestions().length)
      return;
    const today: number = new Time().getEpochDay();
    let day: number = this.selections[index] as number;
    this.dialog.open(DateSelectDialog, {
      width: 'fit-content',
      data: {
        latestAllowed: today,
        start: day,
        startingPosition: day == null ? today : day
      }
    }).beforeClosed().subscribe((result: DateSelectDialogResult) => {
      if(result)
        this.selections[index] = result.start;
    });
  }

  clearPressed(event: any, index: number): void {
    event.stopPropagation();
    if(index < this.getQuestions().length)
      this.selections[index] = null;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.move(true);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.move(false);
        break;
    }
  }
}

export interface AssessmentPreviewDialogData {
  assessment: CompassAssessment;
  startIndex?: number;
}
